'use client';

import { useQueryClient } from '@tanstack/react-query';

import type { _IStickerCard } from '@/app/(football)/[team]/_components/common/StickerCard/_hooks/useStickerCard';
import { trackEvent } from '@/libs/analytics/events';
import { useAppDispatch } from '@/store/client/config';
import { modalActions } from '@/store/client/slices/modalSlice';
import { USE_FOOTBALL_QUERY_KEY, useSetRepresentCardMutation } from '@/store/server/common/football/football.query';
import type { OriginalsName } from '@/store/server/common/originals/originals.dto';
import { useGetOriginalsInitQuery } from '@/store/server/common/originals/originals.query';
import { USE_POINT_QUERY_KEY } from '@/store/server/common/point/point.query';

import { SET_DEFAULT_CARD_MODAL_KEY } from '.';

interface HeaderProps {
  stickerCard: _IStickerCard | null;
  originalsName: OriginalsName;
  isSelectStickerStep: boolean;
}

export default function Header({ stickerCard, originalsName, isSelectStickerStep }: HeaderProps) {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();

  const { data: initData } = useGetOriginalsInitQuery({ originalsNameList: [originalsName] });
  const originalsIdx = initData?.[originalsName]?.originalsIdx ?? 0;

  const { mutate: setRepresentCard, isPending } = useSetRepresentCardMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [USE_FOOTBALL_QUERY_KEY.getRepresentCard] });
      queryClient.invalidateQueries({ queryKey: [USE_FOOTBALL_QUERY_KEY.getRepresentGallery] });
      queryClient.invalidateQueries({ queryKey: [USE_POINT_QUERY_KEY.getMyPoint] });

      dispatch(modalActions.closeLoadingModal());
      dispatch(modalActions.closeAllComponentModalByName({ name: SET_DEFAULT_CARD_MODAL_KEY }));
    },
    onError: (error) => {
      dispatch(modalActions.closeLoadingModal());
      const errorMessage = error instanceof Error ? error.message : undefined;
      dispatch(
        modalActions.openAlertModal({
          title: ['대표카드 설정 실패'],
          description: [errorMessage ?? '다시 시도해주세요.'],
        }),
      );
    },
  });

  const handleCancel = () => {
    // 스티커카드가 없을 경우 즉시 취소
    if (!stickerCard) {
      dispatch(modalActions.closeAllComponentModalByName({ name: SET_DEFAULT_CARD_MODAL_KEY }));
      return;
    }

    dispatch(
      modalActions.openConfirmModal({
        icon: 'danger',
        title: ['적용하지 않고 나가시겠어요?'],
        description: ['적용하지 않으면', '변경된 내용이 모두 사라집니다.'],
        closeText: ['나가기'],
        confirmText: ['계속 수정하기'],
        onClose: () => {
          dispatch(modalActions.closeAllComponentModalByName({ name: SET_DEFAULT_CARD_MODAL_KEY }));
        },
      }),
    );
  };

  const handleApply = () => {
    if (!stickerCard) {
      dispatch(
        modalActions.openAlertModal({
          title: ['먼저 대표카드를 선택해주세요.'],
        }),
      );
      return;
    }

    if (!originalsIdx) return;

    trackEvent({ name: 'apply_rep_card', params: { card_idx: stickerCard.metadataIdx, sticker_count: stickerCard.stickers.length, rep_card: stickerCard.cardName } });

    dispatch(
      modalActions.openConfirmModal({
        icon: 'danger',
        title: ['대표카드로 설정할까요?'],
        closeText: ['아니오'],
        confirmText: ['네'],
        onConfirm: () => {
          dispatch(
            modalActions.openLoadingModal({
              title: ['적용 중...'],
            }),
          );
          setRepresentCard({
            originalsIdx,
            metadataIdx: stickerCard.metadataIdx,
            width: stickerCard.width,
            height: stickerCard.height,
            stickers: stickerCard.stickers,
          });
        },
      }),
    );
  };

  return (
    <div className="flex h-[60px] items-center justify-between px-6">
      <button type="button" onClick={handleCancel} className="cursor-pointer p-0 typo-heading-4 text-white">
        취소
      </button>
      {isSelectStickerStep && (
        <button
          type="button"
          onClick={handleApply}
          disabled={!stickerCard || isPending}
          className="cursor-pointer p-0 typo-heading-4 text-white disabled:opacity-40"
        >
          적용하기
        </button>
      )}
    </div>
  );
}
